import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { m } from 'motion/react';
import { Network, Plus } from 'lucide-react';

import { departments, type DepartmentNode } from '../../api/organization';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { ErrorBanner } from '../../components/ErrorBanner';
import { Field } from '../../components/Field';
import { fieldStyles } from '../../components/styles';
import { SkeletonRows } from '../../components/Skeleton';
import { PageHeader } from '../../components/Surface';
import { RISE, STAGGER } from '../../components/motion';
import { useI18n, useT } from '../../i18n/provider';
import { cn } from '../../lib/cn';
import { useSubmit } from '../auth/useSubmit';
import { useCompanyId } from '../company/useCompany';

/**
 * `13 §3.1`: departments as a tree.
 *
 * Archiving is refused by the server while a department still has active
 * employees or subdepartments, and this screen does not try to predict that —
 * moving people somewhere the manager did not choose is worse than refusing, so
 * the 409 arrives as a message rather than as a disabled button whose reason is
 * invisible.
 */
export function DepartmentsScreen() {
  const t = useT();
  const { plural } = useI18n();
  const companyId = useCompanyId();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const { busy, error, fields, submit } = useSubmit<void>();

  const tree = useQuery({
    queryKey: ['departments', companyId],
    queryFn: () => departments.list(companyId),
    enabled: companyId !== '',
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['departments', companyId] });

  const archive = useMutation({
    mutationFn: (id: string) => departments.archive(id),
    onSuccess: () => void invalidate(),
  });

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const parent = String(form.get('parent_department_id') ?? '');

    void submit(
      async () => {
        await departments.create(companyId, {
          name: String(form.get('name') ?? ''),
          slug: String(form.get('slug') ?? ''),
          mission: String(form.get('mission') ?? '') || null,
          parent_department_id: parent === '' ? null : parent,
        });

        await invalidate();
      },
      () => setAdding(false),
    );
  };

  if (tree.isPending) {
    return <SkeletonRows />;
  }

  if (tree.error) {
    return <ErrorBanner error={tree.error} onRetry={() => void tree.refetch()} />;
  }

  const flat = flatten(tree.data.data);

  return (
    <section className="space-y-6">
      <PageHeader
        title={t('org.departments.title')}
        description={t('org.departments.intro')}
        actions={
          !adding ? (
            <Button icon={<Plus />} onClick={() => setAdding(true)}>
              {t('org.departments.add')}
            </Button>
          ) : null
        }
      />

      {archive.error ? <ErrorBanner error={archive.error} /> : null}

      {adding ? (
        <m.form
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-lg space-y-4 rounded-lg border border-line bg-raised p-5 shadow-sm"
        >
          {error ? <ErrorBanner error={error} /> : null}

          <Field label={t('org.departments.name')} name="name" required error={fields['name']} />
          <Field
            label={t('org.departments.slug')}
            name="slug"
            required
            error={fields['slug']}
            hint={t('org.departments.slugHint')}
          />
          <Field
            label={t('org.departments.mission')}
            name="mission"
            error={fields['mission']}
            hint={t('org.departments.missionHint')}
          />

          <div className="space-y-1.5">
            <label htmlFor="parent_department_id" className="block text-sm font-semibold text-ink">
              {t('org.departments.inside')}
            </label>
            <select
              id="parent_department_id"
              name="parent_department_id"
              className={cn(fieldStyles, 'border-line')}
            >
              <option value="">{t('org.departments.topLevel')}</option>
              {flat.map((row) => (
                <option key={row.department.id} value={row.department.id}>
                  {'— '.repeat(row.depth)}
                  {row.department.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-2">
            <Button type="submit" busy={busy}>
              {t('common.create')}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setAdding(false)}>
              {t('common.cancel')}
            </Button>
          </div>
        </m.form>
      ) : null}

      {flat.length === 0 ? (
        <EmptyState
          icon={<Network />}
          title={t('org.departments.empty')}
          description={t('org.departments.emptyHint')}
        />
      ) : (
        <m.ul
          variants={STAGGER}
          initial="hidden"
          animate="shown"
          className="divide-y divide-line overflow-hidden rounded-lg border border-line bg-raised"
        >
          {flat.map(({ department, depth }) => (
            <m.li
              key={department.id}
              variants={RISE}
              className="flex items-center justify-between gap-4 px-4 py-3 transition-colors hover:bg-hover"
            >
              <div style={{ paddingLeft: `${depth * 1.25}rem` }}>
                <p className="text-sm font-semibold text-ink">{department.name}</p>
                <p className="font-mono text-xs text-ink-faint">{department.slug}</p>
                {department.mission ? <p className="mt-1 text-xs text-ink-soft">{department.mission}</p> : null}
              </div>

              <div className="flex items-center gap-3 text-xs text-ink-faint">
                <span>{plural('org.departments.employees', department.employee_count)}</span>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => archive.mutate(department.id)}
                  busy={archive.isPending && archive.variables === department.id}
                  aria-label={t('org.departments.archiveLabel', { name: department.name })}
                >
                  {t('common.archive')}
                </Button>
              </div>
            </m.li>
          ))}
        </m.ul>
      )}
    </section>
  );
}

/**
 * The tree flattened for a list, keeping the depth so indentation still says
 * what the nesting was. A node whose parent is missing is emitted at the root by
 * the server rather than dropped, so nothing disappears here either.
 */
function flatten(nodes: DepartmentNode[], depth = 0): { department: DepartmentNode; depth: number }[] {
  return nodes.flatMap((node) => [{ department: node, depth }, ...flatten(node.children, depth + 1)]);
}
